/**
 * The command bar (SPEC §7.1): ⌘K, type, ⏎. One overlay, no mode switch:
 * - search: a date phrase first ("last tuesday"), then pages and tags, blocks (with day › parent),
 *   people, companies, meetings; exact title first; titles come instantly from the title index,
 *   blocks from the search worker;
 * - commands (all of them after `>`);
 * - recents before you type.
 * ⏎ lands on a block inside its day or page, ⇧⏎ zooms into it, ⌘⏎ opens it in the right sidebar.
 */
import { Building2, CalendarDays, CornerDownLeft, FileText, Hash, Plus, Text, User, Users } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react'
import {
  hasTag,
  isType,
  makeNode,
  mergedTarget,
  normalizeTitle,
  op,
  parseDatePhrase,
  plainText,
  TAG,
  titleKey,
  titleProblem,
  uuidv7,
  type Node,
} from '@solar/core'
import { searchTitles } from '../client/picker.ts'
import type { Hit } from '../client/search-index.ts'
import type { ClientStore } from '../client/store.ts'
import { dayTitle, todayKey } from './dates.ts'
import { useStore } from './hooks.ts'
import { hrefToBlock, navigate } from './router.ts'

type How = 'open' | 'zoom' | 'sidebar'

interface Item {
  readonly key: string
  readonly group: string
  readonly icon: ReactNode
  readonly label: ReactNode
  readonly hint?: string
  readonly run: (how: How) => void
}

const GROUP_ORDER = ['Go to', 'Best match', 'Recent', 'Pages & tags', 'Blocks', 'People', 'Companies', 'Meetings', 'Commands', 'Create']

/** The day or page a node lives in, and the parent's label for the breadcrumb. */
function place(store: ClientStore, n: Node): { top: Node; crumb: string } {
  let top = n
  while (top.parentId) top = store.g.get(top.parentId) ?? top
  const topLabel = top.kind === 'journal' && top.date ? dayTitle(top.date).title : (top.title ?? '')
  const parent = n.parentId ? store.g.get(n.parentId) : undefined
  const parentLabel = parent && parent.id !== top.id ? plainText(parent.content).slice(0, 40) : ''
  return { top, crumb: parentLabel ? `${topLabel} › ${parentLabel}` : topLabel }
}

/** Matched terms marked in the text (case-insensitive), around the first match. */
function marked(text: string, terms: readonly string[]): ReactNode {
  const ts = [...new Set(terms)].filter(Boolean).sort((a, b) => b.length - a.length)
  if (ts.length === 0) return text.slice(0, 120)
  const re = new RegExp(`(${ts.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi')
  const first = text.search(re)
  const start = first > 50 ? first - 40 : 0
  const slice = (start > 0 ? '…' : '') + text.slice(start, start + 140)
  return slice.split(re).map((part, i) => (i % 2 === 1 ? <mark key={i}>{part}</mark> : part))
}

function groupOf(store: ClientStore, n: Node): string {
  if (hasTag(store.tagEnv, n, mergedTarget(store.g, TAG.person))) return 'People'
  if (hasTag(store.tagEnv, n, mergedTarget(store.g, TAG.company))) return 'Companies'
  return 'Pages & tags'
}

function iconOf(store: ClientStore, n: Node): ReactNode {
  const p = { size: 15, 'aria-hidden': true as const }
  if (n.kind === 'tag' || isType(store.g, n.id)) return <Hash {...p} />
  const g = groupOf(store, n)
  if (g === 'People') return <User {...p} />
  if (g === 'Companies') return <Building2 {...p} />
  return <FileText {...p} />
}

export function CommandBar({ onClose }: { onClose: () => void }) {
  const store = useStore()
  const [q, setQ] = useState('')
  const [active, setActive] = useState(0)
  const [hits, setHits] = useState<{ q: string; hits: Hit[] }>({ q: '', hits: [] })
  const listRef = useRef<HTMLDivElement>(null)

  const indexed = useSyncExternalStore(store.search.subscribe, store.search.getVersion, store.search.getVersion)
  useEffect(() => {
    let alive = true
    const query = q.startsWith('>') ? '' : q
    void store.search.search(query, 30).then((h) => alive && setHits({ q: query, hits: h }))
    return () => {
      alive = false
    }
  }, [q, store, indexed])

  const items = useMemo(() => {
    const out: Item[] = []
    const today = todayKey()
    const openNode = (n: Node) => (how: How) => {
      if (how === 'sidebar') store.openInSidebar({ kind: 'node', id: n.id })
      else navigate(`/n/${n.id}`)
    }
    const openBlock = (n: Node) => (how: How) => {
      if (how === 'sidebar') return store.openInSidebar({ kind: 'node', id: n.id })
      if (how === 'zoom') return navigate(`/n/${n.id}`)
      const { top } = place(store, n)
      store.reveal(n.id)
      navigate(hrefToBlock(top.kind === 'journal' ? top.date : null, top.id, n.id))
    }
    const openDay = (date: string) => () => navigate(date === today ? '/' : `/d/${date}`)
    const commands: Item[] = [
      { key: 'cmd:today', group: 'Commands', icon: <CalendarDays size={15} />, label: 'Today', hint: '⌘⇧T', run: openDay(today) },
      { key: 'cmd:tasks', group: 'Commands', icon: <Hash size={15} />, label: 'Open tasks', run: () => navigate(`/n/${mergedTarget(store.g, TAG.task)}`) },
      { key: 'cmd:calendar', group: 'Commands', icon: <CalendarDays size={15} />, label: 'Calendar', hint: 'right sidebar', run: () => store.openInSidebar({ kind: 'calendar' }) },
      { key: 'cmd:people', group: 'Commands', icon: <Users size={15} />, label: 'People', run: () => navigate(`/n/${mergedTarget(store.g, TAG.person)}`) },
      { key: 'cmd:companies', group: 'Commands', icon: <Building2 size={15} />, label: 'Companies', run: () => navigate(`/n/${mergedTarget(store.g, TAG.company)}`) },
      {
        key: 'cmd:newpage',
        group: 'Commands',
        icon: <Plus size={15} />,
        label: 'New page',
        run: () => {
          let title = 'Untitled'
          for (let i = 2; store.titles.resolve(title); i++) title = `Untitled ${i}`
          const id = uuidv7()
          store.commitOps('create page', [op.create(makeNode({ id, kind: 'page', title, createdAt: Date.now(), updatedAt: Date.now() }))])
          navigate(`/n/${id}`)
        },
      },
      { key: 'cmd:left', group: 'Commands', icon: <CornerDownLeft size={15} />, label: 'Toggle left sidebar', hint: '⌘\\', run: () => store.setSidebar({ left: !store.sidebar.left }) },
      { key: 'cmd:right', group: 'Commands', icon: <CornerDownLeft size={15} />, label: 'Toggle right sidebar', hint: '⌘⇧\\', run: () => store.setSidebar({ open: !store.sidebar.open }) },
      { key: 'cmd:settings', group: 'Commands', icon: <CornerDownLeft size={15} />, label: 'Settings', run: () => navigate('/settings') },
    ]

    if (q.startsWith('>')) {
      const f = q.slice(1).trim().toLowerCase()
      return commands.filter((c) => String(c.label).toLowerCase().includes(f))
    }
    const text = q.trim()
    if (text === '') {
      for (const v of store.recent.slice(0, 12)) {
        if (v.kind === 'day') out.push({ key: `recent:${v.date}`, group: 'Recent', icon: <CalendarDays size={15} />, label: dayTitle(v.date).title, run: openDay(v.date) })
        else {
          const n = store.g.get(v.id)
          if (!n || n.deletedAt !== null) continue
          if (n.title) out.push({ key: `recent:${n.id}`, group: 'Recent', icon: iconOf(store, n), label: n.title, run: openNode(n) })
          else out.push({ key: `recent:${n.id}`, group: 'Recent', icon: <Text size={15} />, label: plainText(n.content).slice(0, 80), hint: place(store, n).crumb, run: openBlock(n) })
        }
      }
      return out.length ? out : commands.slice(0, 3)
    }

    const range = parseDatePhrase(text, today)
    if (range && range.from === range.to) {
      out.push({ key: `day:${range.from}`, group: 'Go to', icon: <CalendarDays size={15} />, label: dayTitle(range.from).title, hint: dayTitle(range.from).weekday, run: openDay(range.from) })
    }
    const key = titleKey(text)
    for (const n of searchTitles(store.titles, text, 12)) {
      const exact = titleKey(n.title!) === key
      out.push({ key: `node:${n.id}`, group: exact ? 'Best match' : groupOf(store, n), icon: iconOf(store, n), label: n.title, run: openNode(n) })
    }
    if (hits.q === q.trim() || hits.q === q) {
      const meeting = mergedTarget(store.g, TAG.meeting)
      for (const h of hits.hits) {
        const n = store.g.get(h.id)
        if (!n || n.deletedAt !== null || n.kind !== 'block') continue
        out.push({
          key: `block:${n.id}`,
          group: hasTag(store.tagEnv, n, meeting) ? 'Meetings' : 'Blocks',
          icon: <Text size={15} />,
          label: marked(plainText(n.content), h.terms),
          hint: place(store, n).crumb,
          run: openBlock(n),
        })
      }
    }
    const lower = text.toLowerCase()
    out.push(...commands.filter((c) => String(c.label).toLowerCase().startsWith(lower)))
    const title = normalizeTitle(text)
    if (!titleProblem(title) && store.titles.resolve(title) === null && !range) {
      out.push({ key: 'create', group: 'Create', icon: <Plus size={15} />, label: `Create page “${title}”`, run: () => store.openTitle(title) })
    }
    out.sort((a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group))
    return out
  }, [q, hits, store])

  useEffect(() => setActive(0), [q])
  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [active])

  const run = (i: number, how: How): void => {
    const it = items[i]
    if (!it) return
    onClose()
    it.run(how)
  }

  let lastGroup = ''
  return (
    <div className="cmd-backdrop" onMouseDown={onClose}>
      <div className="cmd" role="dialog" aria-label="Command bar" onMouseDown={(e) => e.stopPropagation()}>
        <input
          className="cmd-input"
          autoFocus
          aria-label="Search or run a command"
          placeholder="Search, go to a date, or > for commands"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            const mod = /Mac|iPhone|iPad/.test(navigator.userAgent) ? e.metaKey : e.ctrlKey
            if (e.key === 'ArrowDown') (e.preventDefault(), setActive((a) => Math.min(a + 1, items.length - 1)))
            else if (e.key === 'ArrowUp') (e.preventDefault(), setActive((a) => Math.max(a - 1, 0)))
            else if (e.key === 'Enter') (e.preventDefault(), run(active, mod ? 'sidebar' : e.shiftKey ? 'zoom' : 'open'))
            else if (e.key === 'Escape') (e.preventDefault(), e.stopPropagation(), onClose())
          }}
        />
        <div className="cmd-list" role="listbox" aria-label="Results" ref={listRef}>
          {items.map((it, i) => {
            const head = it.group !== lastGroup ? <div className="cmd-group">{it.group}</div> : null
            lastGroup = it.group
            return (
              <div key={it.key}>
                {head}
                <div
                  className={i === active ? 'cmd-item active' : 'cmd-item'}
                  role="option"
                  aria-selected={i === active}
                  onMouseEnter={() => setActive(i)}
                  onClick={(e) => run(i, e.metaKey || e.ctrlKey ? 'sidebar' : e.shiftKey ? 'zoom' : 'open')}
                >
                  <span className="cmd-icon">{it.icon}</span>
                  <span className="cmd-label">{it.label}</span>
                  {it.hint && <span className="cmd-hint">{it.hint}</span>}
                </div>
              </div>
            )
          })}
          {items.length === 0 && <div className="cmd-empty">Nothing found{store.search.ready ? '' : ' yet (search is still indexing)'}</div>}
        </div>
      </div>
    </div>
  )
}

